import { Component, effect, inject, OnInit } from '@angular/core';
import { GlobalsStore } from '../../../stores/globals.state';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MenuItem } from 'primeng/api';
import {
  BarChartConfiguration,
  chartSortingOptions,
  Commune,
} from '../../../models/commune';

import { FileUploadModule } from 'primeng/fileupload';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { NgIf } from '@angular/common';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { HeaderComponent } from '../../atoms/header/header.component';
import { CommuneStore } from '../../../stores/commune/commune.state';
import { DropdownOptionComponent } from '../../atoms/dropdown-option/dropdown-option.component';

@Component({
  selector: 'app-configuration-chart-bar',
  standalone: true,
  imports: [
    CheckboxModule,
    DividerModule,
    FileUploadModule,
    FormFieldWrapperComponent,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    NgIf,
    ReactiveFormsModule,
    ColorsetDropdownComponent,
    DropdownModule,
    RadioButtonModule,
    HeaderComponent,
    DropdownOptionComponent,
  ],
  templateUrl: './configuration-chart-bar.component.html',
  styleUrl: './configuration-chart-bar.component.scss',
})
export class ConfigurationChartBarComponent implements OnInit {
  readonly communeStore = inject(CommuneStore);
  readonly globalStore = inject(GlobalsStore);

  form: FormGroup = new FormGroup({});
  initialized = false;
  items: MenuItem[] = [];
  activeCommune: Commune | null = null;
  barConfiguration?: BarChartConfiguration;

  constructor(private fb: FormBuilder) {
    effect(() => {
      this.communeSetup();
    });
  }

  /**
   * Sets up the active commune by initializing its configuration and related properties.
   * Handles the initialization of atlas configuration and form setup if not already initialized.
   *
   * @return {void} This method does not return any value.
   */
  communeSetup(): void {
    if (!this.activeCommune) {
      this.activeCommune = this.globalStore.activeCommune();
      if (!this.activeCommune) return;
      if (!this.activeCommune.atlasConfig) {
        this.activeCommune.atlasConfig = this.communeStore.baseConfig();
      }
      this.barConfiguration = this.activeCommune.atlasConfig.bar;
    }
    if (this.activeCommune && !this.initialized) {
      this.initForm();
    }
  }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties of a directive.
   * Typically used to perform component initialization logic.
   *
   * @return {void} Does not return any value.
   */
  ngOnInit(): void {
    this.communeSetup();
  }

  /**
   * Initializes the form with default configuration values and sets up a subscription for value changes.
   * The form contains the following fields: displayValues, sort, caption, and colorset.
   * Updates are submitted whenever the form values change.
   *
   * @return {void} Does not return any value.
   */
  initForm(): void {
    this.form = this.fb.group({
      displayValues: [this.barConfiguration?.displayValues || false],
      sort: this.fb.group({
        enable: [this.barConfiguration?.sort?.enable || false],
        property: [this.barConfiguration?.sort?.property || 'label'],
        direction: [this.barConfiguration?.sort?.direction || 'asc'],
      }),
      caption: [this.barConfiguration?.caption || 'name'],
      mode: [this.barConfiguration?.mode || 'vertical'],
      stacked: [this.barConfiguration?.stacked || false],
      colorset: [this.barConfiguration?.colorset],
    });

    this.form.valueChanges.subscribe(() => {
      this.submit();
    });

    this.initialized = true;
  }

  /**
   * Submits the form data and updates the active commune's configuration in the store.
   *
   * The method first checks if there is an active commune. If an active commune exists,
   * it updates the `atlasConfig.bar` property of the active commune with the current form values
   * and subsequently updates the commune in the data store.
   *
   * @return {void} This method does not return a value.
   */
  submit(): void {
    if (!this.activeCommune) return;

    this.activeCommune.atlasConfig.bar = {
      ...this.activeCommune.atlasConfig.bar,
      ...this.form.value,
    };
    this.communeStore.update(this.activeCommune);
  }

  protected readonly chartSortingOptions = chartSortingOptions;
}
